<template>
  <button
    class="r-button"
    :class="classList"
    @click="click"
  >
    <div
      v-if="loading"
      class="spinner"
    />
    <r-icon
      v-if="!suffixIcon && icon && !loading"
      class="r-button__icon"
      :name="getIconName()"
      :color="getIconColor()"
      :color-type="getIconColorType()"
      :size="mini ? 18 : 21"
    />
    <div
      class="r-button__text"
      :style="getColorType()"
    >
      <slot />
    </div>
    <r-icon
      v-if="suffixIcon && icon && !loading"
      class="r-button__icon"
      :name="getIconName()"
      :color="getIconColor()"
      :color-type="getIconColorType()"
      :size="mini ? 18 : 21"
    />
  </button>
</template>

<script>
const types = [
  'default',
  'secondary',
  'danger',
  'warning',
  'primary',
  'success',
  ''
]
const colorTypes = ['primary', 'subhead', 'secondary', 'teritary', '']

export default {
  props: {
    type: {
      type: String,
      validator: type => types.includes(type.toLowerCase()),
      default: 'default'
    },
    colorType: {
      type: String,
      validator: type => colorTypes.includes(type.toLowerCase()),
      default: ''
    },
    mini: {
      type: Boolean,
      default: false
    },
    simple: {
      type: Boolean,
      default: false
    },
    uppercase: {
      type: Boolean,
      default: false
    },
    dropdown: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [String, Object],
      default: () => ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    link: {
      type: Boolean,
      default: false
    },
    bold: {
      type: Boolean,
      default: false
    },
    bolder: {
      type: Boolean,
      default: false
    },
    clean: {
      type: Boolean,
      default: false
    },
    noGap: {
      type: Boolean,
      default: false
    },
    suffixIcon: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    'icon-only'() {
      return !this.$slots.default?.[0]
    },
    classList() {
      const props = [
        'disabled',
        'mini',
        'simple',
        'rounded',
        'uppercase',
        'active',
        'loading',
        'bold',
        'dropdown',
        'bolder',
        'icon-only',
        'link',
        'noGap'
      ]
      const css = {}
      props.forEach(p => {
        css[p] = this[p]
      })
      css[this.type] = true
      return css
    }
  },
  methods: {
    getColorType() {
      if (!this.colorType) return ''
      else return `color: var(--text_${this.colorType})`
    },
    getIconName() {
      return typeof this.icon === 'string' ? this.icon : this.icon?.name || ''
    },
    getIconColor() {
      return typeof this.icon === 'string' ? '' : this.icon?.color || ''
    },
    getIconColorType() {
      return typeof this.icon === 'string' ? '' : this.icon?.type || ''
    },
    click() {
      if (!this.disabled) {
        this.$emit('click')
      }
    }
  }
}
</script>

<style lang="scss">
$danger: var(--accent_error);
$default-bg: var(--button_secondary_bg);
$text: var(--text_primary);
$accent-text: var(--button_primary_bg);
$accent-primary: var(--accent_primary);
$high-contrast: var(--dividers_high_contrast);
$border: var(--dividers_low_contrast);
$default-icon: var(--icons_high_contrast);
$white: var(--white_white);
$success: var(--accent_success);
$primary: var(--button_primary_bg);
$warning: var(--accent_warning);
$selected: var(--accent_selected);
$hover: var(--accent_hover);

.r-button {
  display: flex;
  gap: 0.33rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  border: none;
  padding: 0.25rem 1rem;
  height: 2.25rem;
  cursor: pointer;
  transition: ease 0.15s;
  overflow: hidden;
  max-width: 100%;

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    line-height: 1.25rem;
    font-weight: 400;
    color: $text;
    transition: ease 0.15s;
    user-select: none;

    &:empty {
      display: none;
    }
  }

  .r-icon__svg {
    fill: $white;
  }

  // Default

  &.default {
    background-color: $default-bg;

    .r-icon__svg {
      fill: $default-icon;
    }
  }

  // Colors

  &.danger {
    background-color: $danger;

    .r-icon__svg {
      fill: $white !important;
    }
  }

  &.primary {
    background-color: $primary;

    .r-button__text {
      color: $white;
    }
  }

  &.success {
    background-color: $success;

    .r-button__text {
      color: $white;
    }
  }

  &.warning {
    background-color: $warning;

    .r-button__text {
      color: $white;
    }
  }

  &.secondary {
    background-color: $default-bg;

    &:hover:not(.disabled):not(.simple):not(.loading) {
      background-color: $selected;

      .r-button__text {
        color: $accent-primary;
      }

      .r-icon__svg {
        fill: $accent-primary;
      }
    }

    .r-icon__svg {
      fill: var(--icons_high_contrast);
    }

    &.active {
      background-color: $selected;

      &.dropdown {
        &::after {
          background-color: $accent-primary;
        }
      }

      .r-button__text {
        color: $accent-primary;
      }

      .r-icon__svg {
        fill: $accent-primary;
      }

      &:hover:not(.disabled):not(.loading) {
        background-color: $selected;

        .r-button__text {
          color: $text !important;
        }

        .r-icon__svg {
          fill: var(--icons_high_contrast) !important;
        }
      }
    }
  }

  // All hover

  &:hover:not(.disabled):not(.simple):not(.loading) {
    filter: hue-rotate(-5deg) brightness(0.85);
  }

  // Forms + states

  &.mini {
    max-height: 1.75rem;
    padding: 0.25rem 0.75rem;

    &.icon-only {
      max-width: 1.75rem;
    }
  }

  &.bold {
    .r-button__text {
      font-weight: 500;
    }
  }

  &.bolder {
    .r-button__text {
      font-weight: 600;
    }
  }

  &.uppercase {
    .r-button__text {
      text-transform: uppercase;
      font-size: 12px;
      letter-spacing: 0.5px;
    }
  }

  &.dropdown {
    grid-gap: 0.25rem;

    &.icon-only {
      max-width: 4rem;
      grid-gap: 0rem;
      padding: 0.25rem 0.33rem 0.25rem 0.5rem;
    }

    &:after {
      content: '';
      margin-left: 4px;
      width: 8px;
      height: 4px;
      clip-path: polygon(0 0, 50% 100%, 100% 0);
      background-color: var(--icons_high_contrast);
    }
  }

  &.rounded {
    border-radius: 50%;
  }

  &.link {
    display: inline-flex;
    padding: 0 0.25rem !important;
    height: 1.5rem;

    .r-button__text {
      margin-top: 0.1rem;
      border-bottom: 0.1rem dashed $border;
    }
  }

  &.simple {
    background-color: transparent;
    padding: 0.25rem;
    width: auto;

    .r-button__text {
      color: $text !important;
    }

    &:hover:not(.disabled):not(.loading) {
      .r-button__text {
        color: $accent-text !important;
      }

      &.danger,
      &.warning,
      &.success {
        .r-button__text {
          color: $text !important;
        }
      }
    }

    &.active {
      .r-button__text {
        color: $accent-text;
      }
    }

    &.danger {
      .r-button__text {
        color: $danger !important;
      }
    }

    &.warning {
      .r-button__text {
        color: $warning !important;
      }
    }

    &.success {
      .r-button__text {
        color: $success !important;

        &:hover:not(.disabled):not(.loading) {
          .r-button__text {
            color: $text !important;
          }
        }
      }
    }
  }

  &.icon-only:not(.dropdown) {
    padding: 0;
    width: 2.25rem;
    height: 2.25rem;

    &.simple {
      &:hover {
        background-color: $hover;
      }
    }

    &:hover {
      .r-icon__svg {
        fill: $white;
      }
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.loading {
    transition: all 0.15s ease;

    .spinner {
      &:after {
        content: ' ';
        display: block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        border: 2px solid;
        border-color: $high-contrast transparent $high-contrast transparent;
        animation: spin 0.9s infinite cubic-bezier(0.5, 0, 0.5, 1);
      }
    }

    opacity: 0.8;
    filter: hue-rotate(-5deg) brightness(0.9);
    cursor: progress;
  }

  &.noGap {
    grid-gap: 0 !important;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(1079deg);
  }
}
</style>
